import { useState, type FormEvent } from "react";
import { api, errorMessage } from "../api/client";
import type { Product } from "../api/types";
import { LENGTHS, fitName } from "../lib/fits";


/** "Missing your size?": the shopper adds a size (and fit) the listing has that we didn't show.
 *  It's saved for the product, so the next person who pastes the link sees it too. */
export function AddSize({ product, onAdded }: { product: Product; onAdded: (p: Product, size: string | null, fit: string | null) => void }) {
  const [open, setOpen] = useState(false);
  const [size, setSize] = useState("");
  const [length, setLength] = useState<string | null>(null);
  const [curvy, setCurvy] = useState(false);
  const fit = fitName(length, curvy);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (!open) {
    return (
      <button className="linkish" type="button" onClick={() => setOpen(true)}>
        Missing your size?
      </button>
    );
  }
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const s = size.trim();
    if (!s && !fit) return;
    setBusy(true);
    setErr(null);
    try {
      const { product: p } = await api.addSizes(product.id, { sizes: s ? [s] : [], fit });
      onAdded(p, s || null, fit);
      setOpen(false);
      setSize("");
      setLength(null);
      setCurvy(false);
    } catch (x) {
      setErr(errorMessage(x));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="stack" onSubmit={(e) => void submit(e)}>
      <p className="small muted">Add the size the listing shows. We'll keep it for this piece.</p>
      <div className="row" role="group" aria-label="Fit of the size you're adding">
        {LENGTHS.map((f) => (
          <button key={f} className="chip sm" type="button" aria-pressed={length === f} onClick={() => setLength(length === f ? null : f)}>
            {f}
          </button>
        ))}
        <span className="muted small" aria-hidden="true">
          ·
        </span>
        <button className="chip sm" type="button" aria-pressed={curvy} onClick={() => setCurvy(!curvy)}>
          Curvy
        </button>
      </div>
      <div className="seam">
        <input
          type="text"
          autoComplete="off"
          maxLength={20}
          placeholder="Size, like 23 or XS"
          aria-label="Size the listing shows"
          value={size}
          onChange={(e) => setSize(e.target.value)}
        />
        <button className="btn primary" type="submit" disabled={busy || (!size.trim() && !fit)}>
          Add
        </button>
      </div>
      {err && <p className="hint warn">{err}</p>}
    </form>
  );
}
